/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { CATEGORY_META, initials, fileToBase64 } from '@/lib/utils-cat';
import { MessageCircle, Pencil, LogOut } from 'lucide-react';
import { ExternalLinks } from '@/components/profile/ExternalLinks';
import PostCard from '@/components/PostCard';
import AIStatsCard from '@/components/AIStatsCard';
import { toast } from 'sonner';

const EditProfileDialog = ({ initial, onSaved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: initial?.full_name || '',
    specialty: initial?.specialty || '',
    bio: initial?.bio || '',
    avatar_base64: initial?.avatar_base64 || null,
    phone: initial?.phone || '',
    privacy_email: initial?.privacy_email || 'colleagues',
    privacy_phone: initial?.privacy_phone || 'colleagues',
    privacy_specialty: initial?.privacy_specialty || 'public',
    website_url: initial?.website_url || '',
    linkedin_url: initial?.linkedin_url || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const b64 = await fileToBase64(f);
    set('avatar_base64', b64);
    e.target.value = '';
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/auth/me', form);
      onSaved && onSaved(res.data);
      toast.success(t('save'));
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d?.msg?.replace(/^Value error, /, '')).filter(Boolean).join(' · ') : null;
      toast.error(msg || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" data-testid="profile-edit-button">
          <Pencil className="h-4 w-4 mr-1" /> {t('editProfile')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('editProfile')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-16 w-16">
              {form.avatar_base64 && <AvatarImage src={form.avatar_base64} />}
              <AvatarFallback>{initials(form.full_name)}</AvatarFallback>
            </Avatar>
            <label className="text-xs px-3 py-1.5 rounded-full bg-secondary cursor-pointer">
              {t('addImages')}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-avatar-input" />
            </label>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('fullName')}</Label>
            <Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} data-testid="profile-fullname-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('specialty')}</Label>
            <Input value={form.specialty} onChange={(e) => set('specialty', e.target.value)} data-testid="profile-specialty-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('phone')}</Label>
            <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+39..." data-testid="profile-phone-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('bio')}</Label>
            <Textarea rows={3} value={form.bio} onChange={(e) => set('bio', e.target.value)} data-testid="profile-bio-input" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">{t('websiteUrl')}</Label>
              <Input type="url" inputMode="url" value={form.website_url} onChange={(e) => set('website_url', e.target.value)} placeholder="https://www.studiomedico.it" data-testid="profile-website-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('linkedinUrl')}</Label>
              <Input type="url" inputMode="url" value={form.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)} placeholder="https://www.linkedin.com/in/..." data-testid="profile-linkedin-input" />
            </div>
          </div>
          <div className="space-y-2 pt-2 border-t border-border">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('privacy')}</div>
            {[
              { key: 'privacy_email', label: 'privacyEmail' },
              { key: 'privacy_phone', label: 'privacyPhone' },
              { key: 'privacy_specialty', label: 'privacySpecialty' },
            ].map(({ key, label }) => (
              <div key={key}>
                <Label className="text-xs text-muted-foreground">{t(label)}</Label>
                <div className="mt-1 flex gap-1 flex-wrap">
                  {['public', 'colleagues', 'private'].map(v => (
                    <button key={v} type="button" onClick={() => set(key, v)}
                      className={`text-[11px] px-2 py-1 rounded-full border ${form[key] === v ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border text-muted-foreground'}`}
                      data-testid={`profile-${key}-${v}`}>
                      {t(`privacy_${v}`)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button onClick={save} disabled={saving} data-testid="profile-save-button">{saving ? t('loading') : t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProfilePage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user: me, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const targetId = id || me?.id;
  const isSelf = !id || (me && id === me.id);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const u = isSelf ? me : (await api.get(`/users/${id}`)).data;
        setProfile(u);
        // load their posts: use scope=mine only for self; otherwise fetch general and filter
        if (isSelf) {
          const res = await api.get('/posts?scope=mine');
          setPosts(res.data);
        } else {
          const res = await api.get('/posts?scope=general');
          setPosts((res.data || []).filter(p => p.author?.id === id));
        }
      } catch {
        toast.error(t('errorOccurred'));
      } finally {
        setLoading(false);
      }
    };
    if (targetId) load();
    // eslint-disable-next-line
  }, [id, me]);

  const startChat = async () => {
    try {
      const res = await api.post('/chat/private', { other_user_id: profile.id });
      navigate(`/chat/${res.data.id}`);
    } catch { toast.error(t('errorOccurred')); }
  };

  if (!profile) return <div className="text-sm text-muted-foreground">{t('loading')}</div>;
  const cat = CATEGORY_META[profile.category];
  const Icon = cat?.icon;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-border rounded-2xl p-5 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-24" style={{ background: `linear-gradient(135deg, ${cat?.soft || '#F1F6F5'}, transparent)` }} />
        <div className="relative flex items-start gap-4">
          <Avatar className="h-20 w-20 ring-4 ring-white">
            {profile.avatar_base64 && <AvatarImage src={profile.avatar_base64} />}
            <AvatarFallback className="text-lg" style={{ backgroundColor: cat?.soft, color: cat?.textOnSoft }}>
              {initials(profile.full_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 pt-2">
            <div className="font-display font-semibold text-xl truncate" data-testid="profile-name">{profile.full_name}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1.5 flex-wrap mt-0.5">
              <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5" style={{ backgroundColor: cat?.soft, color: cat?.textOnSoft }}>
                {Icon && <Icon className="h-3 w-3" />} {t(cat?.label || '')}
              </span>
              {profile.specialty && <span>· {profile.specialty}</span>}
            </div>
            {profile.bio && <p className="text-sm mt-2 whitespace-pre-wrap">{profile.bio}</p>}
            <ExternalLinks websiteUrl={profile.website_url} linkedinUrl={profile.linkedin_url} className="mt-2" />
          </div>
          <div className="flex flex-col gap-2">
            {isSelf ? (
              <>
                <EditProfileDialog initial={profile} onSaved={(u) => { setProfile(u); refreshUser(); }} />
                <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} data-testid="profile-logout-button">
                  <LogOut className="h-4 w-4 mr-1" /> {t('logout')}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={startChat} data-testid="profile-message-button">
                <MessageCircle className="h-4 w-4 mr-1" /> {t('message')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <h2 className="font-display font-semibold text-lg">{t('posts')}</h2>
      <div className="space-y-3">
        {loading && <div className="text-sm text-muted-foreground">{t('loading')}</div>}
        {!loading && posts.length === 0 && (
          <div className="text-sm text-muted-foreground">{t('noPostsYet')}</div>
        )}
        {posts.map(p => (
          <PostCard key={p.id} post={p} onChanged={(u) => setPosts(prev => prev.map(x => x.id === u.id ? { ...x, ...u } : x))} onDeleted={(pid) => setPosts(prev => prev.filter(x => x.id !== pid))} />
        ))}
      </div>

      {isSelf && <AIStatsCard />}
    </div>
  );
};

export default ProfilePage;
